// src/features/tasks/components/BulkAddTaskModal/index.jsx
import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import api from '../../../../api/axiosInstance';
import Modal, { ModalFooter } from '../../../../shared/components/ui/Modal';

function BulkAddTaskModal({ isOpen, onClose, onSave, deliverableId, users }) {
  const [formData, setFormData] = useState({
    title: '', priority: 'medium', due_date: '', due_time: ''
  });
  const [selectedUsers,  setSelectedUsers]  = useState([]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [submitting,     setSubmitting]     = useState(false);
  const [error,          setError]          = useState('');

  const userOptions = useMemo(() =>
    users.map(user => ({
      value: user.id,
      label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username,
    })),
  [users]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => setAttachmentFile(e.target.files[0] || null);

  const handleClose = () => {
    setFormData({ title: '', priority: 'medium', due_date: '', due_time: '' });
    setSelectedUsers([]);
    setAttachmentFile(null);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || selectedUsers.length === 0) {
      setError('عنوان المهمة واختيار المستخدمين حقول إلزامية.');
      return;
    }
    setSubmitting(true);
    setError('');

    const payload = new FormData();
    payload.append('title',       formData.title);
    payload.append('priority',    formData.priority);
    payload.append('deliverable', deliverableId);
    if (formData.due_date) payload.append('due_date',    formData.due_date);
    if (formData.due_time) payload.append('due_time',    formData.due_time);
    if (attachmentFile)    payload.append('attachment',  attachmentFile);
    selectedUsers.forEach(user => payload.append('assigned_to_ids', user.value));

    try {
      await api.post('/tasks/bulk-create/', payload);
      handleClose();
      onSave();
    } catch (err) {
      setError('فشل في إنشاء المهام المكررة.');
      console.error(err.response?.data || err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="إضافة مهمة مكررة لعدة مستخدمين"
      footer={
        <ModalFooter
          onClose={handleClose}
          onSave={handleSubmit}
          saveLabel={`إنشاء (${selectedUsers.length}) مهمة`}
          loading={submitting}
        />
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>

        <div>
          <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 'var(--font-semibold)', fontSize: 'var(--font-size-sm)' }}>
            عنوان المهمة *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleFormChange}
            placeholder="أدخل عنوان المهمة..."
            required
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 'var(--font-semibold)', fontSize: 'var(--font-size-sm)' }}>
            إسناد إلى *
          </label>
          <Select
            isMulti
            options={userOptions}
            onChange={setSelectedUsers}
            value={selectedUsers}
            placeholder="اختر موظفًا أو أكثر..."
            isRtl
            styles={{
              control: (base) => ({
                ...base,
                borderColor: 'var(--border-default)',
                borderRadius: 'var(--radius-md)',
                minHeight: '42px',
                fontSize: 'var(--font-size-sm)',
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999,
                fontSize: 'var(--font-size-sm)',
              }),
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 'var(--font-semibold)', fontSize: 'var(--font-size-sm)' }}>
              الأولوية
            </label>
            <select name="priority" value={formData.priority} onChange={handleFormChange}>
              <option value="medium">متوسطة</option>
              <option value="high">عالية</option>
              <option value="low">منخفضة</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 'var(--font-semibold)', fontSize: 'var(--font-size-sm)' }}>
              تاريخ الاستحقاق
            </label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={handleFormChange}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 'var(--font-semibold)', fontSize: 'var(--font-size-sm)' }}>
              وقت الاستحقاق
            </label>
            <input
              type="time"
              name="due_time"
              value={formData.due_time}
              onChange={handleFormChange}
            />
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: 'var(--space-2)', fontWeight: 'var(--font-semibold)', fontSize: 'var(--font-size-sm)' }}>
            إضافة مرفق (اختياري)
          </label>
          <input
            type="file"
            name="attachment"
            onChange={handleFileChange}
            accept="image/*"
          />
        </div>

        {error && (
          <p style={{
            color: 'var(--color-danger-text)',
            background: 'var(--color-danger-subtle)',
            border: '1px solid var(--color-danger-muted)',
            borderRight: '3px solid var(--color-danger)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-semibold)',
            margin: 0,
          }}>
            {error}
          </p>
        )}

      </div>
    </Modal>
  );
}

export default BulkAddTaskModal;